import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { businessInfo, services } from '../data/mock';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredContact: 'email',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setIsSubmitted(true);
        toast.success(data.message || 'Your message has been sent! Check your email for confirmation.');
        
        // Reset form after delay
        setTimeout(() => {
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            preferredContact: 'email',
            service: '',
            message: '',
          });
          setIsSubmitted(false);
        }, 5000);
      } else {
        toast.error(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Contact Us - Schedule Your Consultation Today"
        description="Ready to begin your journey toward healing? Contact Calling The Deep Counseling Services in Lebanon, PA. Located at Wallace House Suite C, 336 Cumberland Street. Call 717-454-3802 or fill out our contact form."
        keywords="contact therapist Lebanon PA, schedule counseling appointment, therapy consultation Lebanon Pennsylvania, marriage counselor contact"
        url="/contact"
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-teal-50 via-white to-slate-50">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-1/2 h-full bg-gradient-to-b from-teal-100/20 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <img 
              src={businessInfo.logo} 
              alt={businessInfo.name}
              className="h-32 w-auto mx-auto mb-6"
            />
            <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Getting Started</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mt-2 mb-6">
              Take the First Step
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Reaching out takes courage. We're here to make the process as comfortable as possible. Fill out the form below to schedule your consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8 sm:p-12">
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">Request a Consultation</h2>
                  <p className="text-slate-600 mb-8">Fill out the form below and we'll get back to you within 24-48 business hours.</p>
                  
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="h-10 w-10 text-teal-600" />
                      </div>
                      <h3 className="text-2xl font-semibold text-slate-800 mb-2">Message Sent!</h3>
                      <p className="text-slate-600">Thank you for reaching out. We'll be in touch soon.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name Fields */}
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="firstName" className="text-slate-700">First Name *</Label>
                          <Input
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                            className="border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                            placeholder="Your first name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName" className="text-slate-700">Last Name *</Label>
                          <Input
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                            className="border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                            placeholder="Your last name"
                          />
                        </div>
                      </div>

                      {/* Contact Fields */}
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-slate-700">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                            placeholder="your@email.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-slate-700">Phone Number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            className="border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>

                      {/* Preferred Contact Method */}
                      <div className="space-y-2">
                        <Label className="text-slate-700">Preferred Contact Method</Label>
                        <div className="flex gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="preferredContact"
                              value="email"
                              checked={formData.preferredContact === 'email'}
                              onChange={handleChange}
                              className="text-teal-600 focus:ring-teal-500"
                            />
                            <span className="text-slate-600">Email</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="preferredContact"
                              value="phone"
                              checked={formData.preferredContact === 'phone'}
                              onChange={handleChange}
                              className="text-teal-600 focus:ring-teal-500"
                            />
                            <span className="text-slate-600">Phone</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="preferredContact"
                              value="text"
                              checked={formData.preferredContact === 'text'}
                              onChange={handleChange}
                              className="text-teal-600 focus:ring-teal-500"
                            />
                            <span className="text-slate-600">Text</span>
                          </label>
                        </div>
                      </div>

                      {/* Service Interest */}
                      <div className="space-y-2">
                        <Label htmlFor="service" className="text-slate-700">Service of Interest</Label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                        >
                          <option value="">Select a service...</option>
                          {services.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.title}
                            </option>
                          ))}
                          <option value="not-sure">Not sure yet</option>
                        </select>
                      </div>

                      {/* Message */}
                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-slate-700">Tell us a little about what brings you here</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 resize-none"
                          placeholder="Share as much or as little as you're comfortable with..."
                        />
                      </div>

                      {/* Privacy Notice */}
                      <p className="text-sm text-slate-500">
                        Your information is kept strictly confidential. By submitting this form, you agree to be contacted regarding your inquiry.
                      </p>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            Send Message
                            <Send className="h-5 w-5" />
                          </span>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Information Sidebar */}
            <div className="space-y-6">
              {/* Direct Contact */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-6">Contact Directly</h3>
                  <div className="space-y-5">
                    <a href={`tel:${businessInfo.phone}`} className="flex items-start gap-4 hover:opacity-80 transition-opacity">
                      <Phone className="h-5 w-5 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Phone</p>
                        <p className="text-teal-100">{businessInfo.phone}</p>
                      </div>
                    </a>
                    <a href={`mailto:${businessInfo.email}`} className="flex items-start gap-4 hover:opacity-80 transition-opacity">
                      <Mail className="h-5 w-5 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Email</p>
                        <p className="text-teal-100">{businessInfo.email}</p>
                      </div>
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Office Location */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <MapPin className="h-5 w-5 text-teal-600" />
                    <h3 className="text-lg font-semibold text-slate-800">Office Location</h3>
                  </div>
                  <p className="text-slate-600">
                    Wallace House Suite C<br />
                    336 Cumberland Street<br />
                    {businessInfo.address.city}, {businessInfo.address.state} {businessInfo.address.zip}
                  </p>
                </CardContent>
              </Card>

              {/* Office Hours */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="h-5 w-5 text-teal-600" />
                    <h3 className="text-lg font-semibold text-slate-800">Office Hours</h3>
                  </div>
                  <div className="text-center py-2">
                    <p className="text-lg font-semibold text-slate-800">By Appointment Only</p>
                    <p className="text-slate-600 text-sm mt-1">Flexible scheduling available, including evening hours</p>
                  </div>
                </CardContent>
              </Card>

              {/* What to Expect */}
              <Card className="border-0 shadow-lg bg-slate-50">
                <CardContent className="p-8">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">What Happens Next?</h3>
                  <ol className="space-y-3">
                    {[
                      "We'll review your inquiry",
                      "Reach out within 24-48 hours",
                      "Schedule a brief consultation call",
                      "Book your first session"
                    ].map((step, index) => (
                      <li key={index} className="flex items-start gap-3 text-slate-600">
                        <span className="w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="text-sm">{step}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Common Questions</h2>
            <p className="text-slate-600">Before your first session</p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "What can I expect in my first session?",
                answer: "Your first session is an opportunity for us to get to know each other. We'll discuss what brings you to therapy, your history, and your goals. It's also a chance for you to ask questions and see if we're a good fit."
              },
              {
                question: "How long are therapy sessions?",
                answer: "Sessions last for 50-55 minutes."
              },
              {
                question: "Is therapy confidential?",
                answer: "Yes, confidentiality is fundamental to the therapeutic relationship and is strictly adhered to per HIPAA. There are a few legal exceptions such as the client disclosing anything that causes the therapist to be concerned about the safety of the client or anyone else."
              },
              {
                question: "What are your rates and do you accept insurance?",
                answer: "The rate per session at Calling The Deep is $80. We do not accept insurance. Some insurance companies will reimburse for therapy sessions and we are able to provide you with a document to submit to your insurance company for this purpose."
              }
            ].map((faq, index) => (
              <Card key={index} className="border-0 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{faq.question}</h3>
                  <p className="text-slate-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contact;
